// i18n
import { translateInComponent } from 'infras/i18n';
import translations from 'presentations/views/components/pages/EventTypePage/i18n.json';

import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import EventTypeHeader from 'presentations/views/components/organisms/EventTypeHeader';
import EventMainVisual from 'presentations/views/components/organisms/EventMainVisual';
import Headline from 'presentations/views/components/atoms/Headline';
import RequiredServiceList from 'presentations/views/components/organisms/RequiredServiceList';
import { useEventTypePage } from './hooks/useEventTypePage';
import { SharedEventDetailItem } from 'presentations/api/schema/event';
import EventTypeFooter from 'presentations/views/components/organisms/EventTypeFooter';
import Button from 'presentations/views/components/atoms/Button';
import UserEventTypeTagList from 'presentations/views/components/organisms/UserEventTypeTagList';
import EventReservationView, {
  Steps,
} from 'presentations/views/components/organisms/EventReservationView';
import EventTypeDateForm from 'presentations/views/components/organisms/features/eventType/calendarView/EventTypeDateForm';
import EventTypeTimeForm from 'presentations/views/components/organisms/features/eventType/calendarView/EventTypeTimeForm';
import EventTypeDateTimeForm from 'presentations/views/components/organisms/features/eventType/calendarView/EventTypeDateTimeForm';
import EventTypeWeeklyForm from 'presentations/views/components/organisms/features/eventType/calendarView/EventTypeWeeklyForm';
import EventTypeListTimeForm from 'presentations/views/components/organisms/features/eventType/calendarView/EventTypeListTimeForm';
import EventTypeGridTimeForm from 'presentations/views/components/organisms/features/eventType/calendarView/EventTypeGridTimeForm';
import { LocationType } from 'domains/models/event_type/locationType';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import CalendarListDialog from 'presentations/views/components/organisms/features/calendarIntegration/CalendarListDialog';
import ParticipantsListDialog from 'presentations/views/components/organisms/ParticipantsListDialog';
import { SharedEventTypeItem } from 'presentations/api/schema/event_type';
import { TeamItem } from 'presentations/api/schema/team';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { ViewerItem } from 'presentations/api/schema/user';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';
import InquiryFormDialog from 'presentations/views/components/organisms/InquiryFormDialog';
import Snackbar, {
  SeverityType,
} from 'presentations/views/components/atoms/Snackbar';
import EventTypeBlockReasonDialog from 'presentations/views/components/organisms/features/eventType/dialog/EventTypeBlockReasonDialog';
import { Status as EventStatus } from 'domains/models/event/status';
import { Status } from 'domains/models/event_type/status';
import { PhoneType } from 'domains/models/event_type_location/phoneType';
import { EventTypeType } from 'domains/models/event_type/eventTypeType';
import { SyntheticEvent, useMemo, useState } from 'react';
import { UserType } from 'domains/models/event_type_user/user_type';
import {
  spacing,
  mediaMax,
  borderRadius,
  fontSize,
  fontWeight,
} from 'presentations/views/styles';
import {
  black,
  blueGrayPale,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import styled from '@emotion/styled';
import JicooBrandingFeature from 'domains/models/subscription/features/jicoo_branding';
import PaymentFeature from 'domains/models/subscription/features/payment';
import { useActiveStepForm } from './hooks/useActiveStepForm';
import { getDateTime } from 'infras/datetime';
import { useEventPopover } from './hooks/useEventPopover';
import EventSummary from './components/EventSummary';
import { useRouter } from 'next/router';
import ServiceRestrictedDialog from '../../organisms/ServiceRestrictedDialog';
import AdminToolbar, { PreviewMode } from './AdminToolbar';
import { hostEventTypeEditPath } from 'presentations/routes/path';

export interface Props {
  viewer: ViewerItem | null;
  eventType: SharedEventTypeItem;
  event: SharedEventDetailItem | null; // nullでなければ Event Edit
  team: TeamItem;
  locationIds: LocationType[];
  host: EventTypeUserListItem;
  googleUrl: string;
  microsoftUrl: string;
  locale?: string;
  onetimeLinkId?: string;
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

export default function Container(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const [previewMode, setPreviewMode] = useState<PreviewMode>(PreviewMode.Host);
  const viewer = previewMode === PreviewMode.Guest ? null : props.viewer;

  const { state, actions } = useEventTypePage({
    viewer: viewer,
    eventType: props.eventType,
    event: props.event,
    team: props.team,
    locationIds: props.locationIds,
    host: props.host,
    isWidget: false,
    locale: props.locale,
    onetimeLinkId: props.onetimeLinkId,
    name: props.name,
    email: props.email,
    phoneNumber: props.phoneNumber,
  });

  const onChangePreviewMode = (e: SyntheticEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    const mode = Number(value);
    setPreviewMode(mode);
  };

  const eventReservationViewWidth = [CalendarViewType.Month].includes(
    state.viewType,
  )
    ? '386px'
    : '696px';
  const isEventTypeCustom =
    props.eventType.eventTypeType === EventTypeType.Custom;
  // 予約編集ではないを前提として、イベントタイプのisReservedがtrueかつカスタム日程調整、もしくは予約ページが無効なら予約受付終了
  // あるいはイベントがキャンセル済みなら予約受付終了
  const eventName = props.event?.name;
  const isEventCancelled = props.event?.status === EventStatus.Cancel;
  const isOnetimeLink = props.onetimeLinkId !== undefined;
  const isReserved =
    (!eventName &&
      ((props.eventType.isReserved && (isEventTypeCustom || isOnetimeLink)) ||
        props.eventType.status === Status.Disable)) ||
    (state.calendarIntegrationError &&
      Object.keys(state.calendarIntegrationError).length > 0) ||
    isEventCancelled;

  const requiredServices = useMemo(
    () =>
      props.eventType.services
        .filter((service) => !!service.isRequired)
        .sort((a, b) => a.sort - b.sort),
    [props.eventType.services],
  );

  const jicooBrandingFeature = new JicooBrandingFeature({
    planVersion: props.eventType.planVersion,
  });
  const paymentFeature = new PaymentFeature({
    planVersion: props.eventType.planVersion,
  });

  const eventTypeUsers = props.eventType.users.filter(
    (e) => e.userType === UserType.Host,
  );

  const isFullWidth = useMemo(
    () =>
      [
        CalendarViewType.CalendarWeekLegacy,
        CalendarViewType.WeekCalendar,
        CalendarViewType.Week,
      ].includes(state.viewType),
    [state.viewType],
  );

  const eventPopover = useEventPopover({
    viewer: viewer,
    timeZone: state.timeZone,
  });

  const reservationView = () => {
    if (state.activeStep === Steps.Date) {
      if (state.viewType === CalendarViewType.Month) {
        return (
          <EventTypeDateForm
            isOpen={state.isOpenReservation}
            selectedDate={state.selectedDate}
            onDateSelect={actions.onDateSelect}
            onChangeMonth={actions.onChangeMonth}
            timeZones={state.timeZones}
            timeZone={state.timeZone}
            defaultDate={state.defaultDate}
            updateTimeZone={actions.updateTimeZone}
            isLoading={!state.isInitLoaded || state.isLoading}
            availableDays={state.availableDays}
            color={props.eventType.color}
            startWeekDay={state.startWeekDay}
            isLightColor={state.isLightColor}
            isHostDiagnosticMode={state.isHostDiagnosticMode}
            isFetching={state.isFetching}
          />
        );
      } else if (state.viewType === CalendarViewType.ListTime) {
        return (
          <EventTypeListTimeForm
            isLoading={!state.isInitLoaded || state.isLoading}
            isViewMoreLoading={state.isLoadingViewMore}
            timeZones={state.timeZones}
            timeZone={state.timeZone}
            duration={props.eventType.duration}
            updateTimeZone={actions.updateTimeZone}
            calendarDateList={state.calendarDateList}
            color={props.eventType.color}
            isRevealGuestUser={props.eventType.isRevealGuestUser}
            isShowViewerCalendar={state.isShowViewerCalendar}
            isHostDiagnosticMode={state.isHostDiagnosticMode}
            onSelect={actions.onTimeSelect}
            onClickBlockTime={actions.onClickBlockTime}
            onClickEventName={eventPopover.actions.onClickAnchor}
            onClickViewMore={actions.onClickViewMoreListViewType}
          />
        );
      } else if (state.viewType === CalendarViewType.GridTime) {
        return (
          <EventTypeGridTimeForm
            isLoading={!state.isInitLoaded || state.isLoading}
            isViewMoreLoading={state.isLoadingViewMore}
            timeZones={state.timeZones}
            timeZone={state.timeZone}
            duration={props.eventType.duration}
            updateTimeZone={actions.updateTimeZone}
            calendarDateList={state.calendarDateList}
            color={props.eventType.color}
            isRevealGuestUser={props.eventType.isRevealGuestUser}
            isShowViewerCalendar={state.isShowViewerCalendar}
            isHostDiagnosticMode={state.isHostDiagnosticMode}
            onSelect={actions.onTimeSelect}
            onClickBlockTime={actions.onClickBlockTime}
            onClickEventName={eventPopover.actions.onClickAnchor}
            onClickViewMore={actions.onClickViewMoreListViewType}
          />
        );
      } else {
        return (
          <EventTypeDateTimeForm
            isOpen={state.isOpenReservation}
            selectedDate={state.selectedDate}
            onDateSelect={actions.onDateSelect}
            onChangeMonth={actions.onChangeMonth}
            timeZones={state.timeZones}
            timeZone={state.timeZone}
            defaultDate={state.defaultDate}
            updateTimeZone={actions.updateTimeZone}
            isLoading={!state.isInitLoaded || state.isLoading}
            availableDays={state.availableDays}
            color={props.eventType.color}
            startWeekDay={state.startWeekDay}
            isLightColor={state.isLightColor}
            selectedDatetime={state.selectedDatetime}
            timeList={state.selectedTimeList}
            onTimeSelect={actions.onTimeSelect}
            calendarViewType={state.viewType}
            isRevealGuestUser={props.eventType.isRevealGuestUser}
            isShowViewerCalendar={state.isShowViewerCalendar}
            isHostDiagnosticMode={state.isHostDiagnosticMode}
            onClickBlockTime={actions.onClickBlockTime}
            isFetching={state.isFetching}
          />
        );
      }
    }
    if (state.activeStep === Steps.DateWeekly) {
      return (
        <EventTypeWeeklyForm
          viewType={state.viewType}
          eventType={props.eventType}
          onDateSelect={actions.onChangeMonth}
          selectedStartDate={state.selectedStartDate}
          selectedDatetime={state.selectedDatetime}
          allTimeLists={state.allTimeLists}
          timeLists={state.selectedTimeLists}
          onTimeSelect={actions.onTimeSelect}
          timeZones={state.timeZones}
          timeZone={state.timeZone}
          updateTimeZone={actions.updateTimeZone}
          isLoading={
            !state.isInitLoaded ||
            !state.isWeeklyDataInitialized ||
            state.isLoading
          }
          isLightColor={state.isLightColor}
          viewerBlocks={state.viewerBlocks}
          viewerTeamUser={state.viewerTeamUser}
          isRevealGuestUser={props.eventType.isRevealGuestUser}
          isShowViewerCalendar={state.isShowViewerCalendar}
          isHostDiagnosticMode={state.isHostDiagnosticMode}
          onClickBlockTime={actions.onClickBlockTime}
          onClickEvent={eventPopover.actions.onClickAnchor}
          startWeekDay={state.startWeekDay}
          isFetching={state.isFetching}
          displayWeekdays={state.displayWeekdays}
        />
      );
    }
    if (state.activeStep === Steps.Time) {
      return (
        <EventTypeTimeForm
          selectedDate={state.selectedDate}
          selectedDatetime={state.selectedDatetime}
          timeList={state.selectedTimeList}
          onTimeSelect={actions.onTimeSelect}
          timeZone={state.timeZone}
          isLoading={state.isLoading}
          color={props.eventType.color}
          isLightColor={state.isLightColor}
          isRevealGuestUser={props.eventType.isRevealGuestUser}
          isShowViewerCalendar={state.isShowViewerCalendar}
          isHostDiagnosticMode={state.isHostDiagnosticMode}
          onClickBlockTime={actions.onClickBlockTime}
        />
      );
    }

    return null;
  };

  const eventReservationView = (
    <EventReservationView
      viewer={viewer}
      calendarDateList={state.calendarDateList}
      teamSlug={props.team.slug}
      isOpen={state.isOpenReservation}
      isInitLoaded={state.isInitLoaded}
      title={t(`headline ${state.activeStep}`)}
      onClickBack={actions.onClickBack}
      isShowBack={state.activeStep !== state.initialStep}
      containerRef={state.eventReservationRef}
      hasTeamHeaderLogo={state.hasTeamHeaderLogo}
      accountConnectMode={state.accountConnectMode}
      eventTypeSlug={props.eventType.slug}
      color={props.eventType.color}
      onClickCalendarSetting={actions.onClickCalendarSetting}
      editingEventUid={props.event?.uid}
      activeStep={state.activeStep}
      calendarViewType={state.viewType}
      hiddenConnectRecommend={
        jicooBrandingFeature.isValid(props.eventType.planId) &&
        !props.eventType.isShowPromotion
      }
      isOwnEventType={state.isOwnEventType}
      isHostDiagnosticMode={state.isHostDiagnosticMode}
      onClickHostDianosticMode={actions.onClickHostDianosticMode}
      form={reservationView()}
      hostImage={state.hostImage}
      onChangeViewType={actions.onChangeViewType}
      isShowViewerCalendar={state.isShowViewerCalendar}
      onChangeIsShowViewerCalendar={actions.onChangeIsShowViewerCalendar}
    />
  );

  const activeStepForm = useActiveStepForm({
    onClickBack: actions.onClickBack,
    color: props.eventType.color,
    step: state.activeStep,
    hostContent: {
      host: props.host,
      hostOperationType: props.eventType.hostOperationType,
      hostImage: state.hostImage,
      teamName: props.team.name,
      teamImage: state.teamImage,
      eventName: eventName || props.eventType.name,
      otherUsersCount: eventTypeUsers.length - 1,
      scheduleText: state.selectedDatetime
        ? `${getDateTime(state.selectedDatetime, state.timeZone).toFormat(
            t('schedule text format', {
              endTimeText:
                state.selectedDatetime
                  ?.plus({ minutes: props.eventType.duration })
                  ?.toFormat('HH:mm') ?? '',
            }),
            {
              locale: router.locale,
            },
          )}`
        : '',
      timeZoneText: state.timeZone,
      location: state.selectedLocationId
        ? {
            locationType: state.selectedLocationId,
            address: state.address,
            access: state.access,
            customLocation: state.customLocation,
          }
        : undefined,
      isRequest: props.eventType.setting.isBookingRequest,
    },
    locationForm: {
      isLoading: state.isLoading,
      locationIds: props.locationIds,
      selectedLocationId: state.selectedLocationId,
      onSelectLocationType: actions.onSelectLocationType,
      customLocation: state.customLocation,
      address: state.address,
      color: props.eventType.color,
    },
    serviceForm: {
      color: props.eventType.color,
      isLightColor: state.isLightColor,
      isLoading: state.isLoading,
      services: props.eventType.services,
      selectedServices: state.selectedServices,
      onSelectService: actions.onSelectService,
      onSubmit: actions.onSubmitServices,
      isDisabled: state.isDisabled,
    },
    guestContactForm: {
      email: state.email,
      ccEmails: state.ccEmails,
      message: state.message,
      name: state.name,
      phoneNumber: state.phoneNumber,
      textRemindCountry: state.textRemindCountry,
      textRemindNumber: state.textRemindNumber,
      company: state.company,
      locationId: state.selectedLocationId,
      onChange: actions.onChange,
      onChangeTextRemindNumber: actions.onChangeTextRemindNumber,
      onBlur: actions.onBlur,
      onInputChangeTag: actions.onInputChangeTag,
      onSubmitTag: actions.onSubmitTag,
      onDeleteTag: actions.onDeleteTag,
      isLoading: state.isLoading,
      onSubmit: actions.onSubmitContact,
      isDisabled: state.isDisabled,
      errors: state.errors,
      customQuestions: state.customQuestions,
      customQuestionAnswers: state.customQuestionAnswers,
      customOtherAnswers: state.customOtherAnswers,
      onClickOtherAnswer: actions.onClickOtherAnswer,
      isCompanyHidden: props.eventType.setting.isCompanyHidden,
      isPhoneNumberRequired:
        state.selectedLocationId === LocationType.Phone &&
        props.eventType?.location?.phoneType === PhoneType.CallFromHost,
      isTextRemindNumberRequired:
        props.eventType?.setting?.isTextRemindNumberRequired,
      isTextRemind: props.eventType.setting.isTextRemind,
      isCompanyRequired: props.eventType.setting.isCompanyRequired,
      isCcEmailsHidden: props.eventType.setting.isCcEmailsHidden,
      isCcEmailsRequired: props.eventType.setting.isCcEmailsRequired,
      isMessageHidden: props.eventType.setting.isMessageHidden,
      isMessageRequired: props.eventType.setting.isMessageRequired,
      color: props.eventType.color,
      isLightColor: state.isLightColor,
      selectedServices: state.selectedServices,
      isPaidServiceSelected: state.isPaidServiceSelected,
      isProvidedContact: state.isProvidedContact,
      isRequest: props.eventType.setting.isBookingRequest,
      isSelectedServicesHidePriceOnly: state.isSelectedServicesHidePriceOnly,
    },
    selectPaymentMethodForm: {
      selectedServices: state.selectedServices,
      isPaymentCard:
        Boolean(props.eventType.stripeAccountId) &&
        props.eventType.setting.isPaymentCard &&
        paymentFeature.isValid(state.planId),
      isPaymentOnsite: props.eventType.setting.isPaymentOnsite,
      isLoading: state.isLoading,
      onSelectPaymentMethod: actions.onSelectPaymentMethod,
      color: props.eventType.color,
    },
    paymentPreviewForm: {
      isLoading: state.isLoading,
      isDisabled: false,
      onClick: actions.onClickTerm,
      onSubmit: actions.onSubmitPayment,
      selectedDatetime: state.selectedDatetime!,
      selectedServices: state.selectedServices,
      selectedPaymentMethod: state.selectedPaymentMethod!,
      timeZone: state.timeZone,
      eventType: props.eventType,
      color: props.eventType.color,
      isLightColor: state.isLightColor,
      teamSlug: props.team.slug,
    },
    eventReservationView,
  });

  return (
    <ServicePageTemplate
      header={
        <EventTypeHeader
          viewer={viewer}
          headerTeamImage={props.team.headerTeamImage}
          isShowPromotion={props.eventType.isShowPromotion}
        />
      }
      isHiddenFooter
      isHiddenContentFooter
      breadcrumbs={[]}
      showHeaderCalendarError={state.hasCalendarIntegrationError}
      notificationBar={
        (state.isHost || previewMode === PreviewMode.Guest) && (
          <AdminToolbar
            previewMode={previewMode}
            isDebuggable={state.isAdmin}
            onChangePreviewMode={onChangePreviewMode}
            url={hostEventTypeEditPath(props.eventType.uid)}
            isInitLoaded={state.isInitLoaded}
            isHostDiagnosticMode={state.isHostDiagnosticMode}
            onClickHostDianosticMode={actions.onClickHostDianosticMode}
          />
        )
      }
    >
      {eventPopover.state.popover}
      <PageContainer>
        <MainContainer isFullWidth={isFullWidth}>
          <BgWrapper>
            {activeStepForm}
            {activeStepForm == null && (
              <>
                <EventMainVisualContainer>
                  <EventMainVisual coverImage={props.eventType.coverImage} />
                </EventMainVisualContainer>
                {!isReserved && (
                  <>
                    <EventHostSummaryBoxDesktopVisible>
                      <EventSummary
                        event={props.event}
                        coverImage={props.eventType.coverImage}
                        eventType={props.eventType}
                        host={props.host}
                        hostImage={state.hostImage}
                        teamName={props.team.name}
                        teamImage={state.teamImage}
                        eventTypeUsers={eventTypeUsers}
                      />
                    </EventHostSummaryBoxDesktopVisible>
                    <EventTypeContentBox
                      calendarViewType={state.viewType}
                      isTopBorder={!props.eventType.coverImage}
                    >
                      <StyledEventSummaryViewContainer>
                        <EventHostSummaryBoxDesktopHidden>
                          <EventSummary
                            event={props.event}
                            coverImage={props.eventType.coverImage}
                            eventType={props.eventType}
                            host={props.host}
                            hostImage={state.hostImage}
                            teamName={props.team.name}
                            teamImage={state.teamImage}
                            eventTypeUsers={eventTypeUsers}
                          />
                        </EventHostSummaryBoxDesktopHidden>
                        <EventInfo>
                          <TitleHeadline variant="h1">
                            {eventName || props.eventType.name}
                          </TitleHeadline>
                          <UserEventTypeTagList
                            location={props.eventType.location}
                            duration={props.eventType.duration}
                            isRequest={props.eventType.setting.isBookingRequest}
                            isShowGuest
                          />
                        </EventInfo>
                        {props.eventType.description && (
                          <DescriptionContainer>
                            <div className="jicoo-editor-body jicoo-editor-view">
                              <Description
                                dangerouslySetInnerHTML={{
                                  __html: props.eventType.description,
                                }}
                              />
                            </div>
                          </DescriptionContainer>
                        )}
                        {requiredServices.length > 0 && (
                          <StyledRequiredServiceContainer>
                            <SectionTitle>{t('basic service')}</SectionTitle>
                            <RequiredServiceList
                              services={requiredServices}
                              isProvidedContact={state.isProvidedContact}
                              restrictServiceUsages={
                                state.restrictServiceUsages
                              }
                            />
                          </StyledRequiredServiceContainer>
                        )}
                      </StyledEventSummaryViewContainer>
                      {!state.isFetchFailed ? (
                        <StyledEventReservationViewContainer
                          calendarViewType={state.viewType}
                          width={eventReservationViewWidth}
                        >
                          <StyledEventReservationViewContent
                            calendarViewType={state.viewType}
                          >
                            {eventReservationView}
                          </StyledEventReservationViewContent>
                        </StyledEventReservationViewContainer>
                      ) : (
                        <FailedView />
                      )}
                    </EventTypeContentBox>
                  </>
                )}
                {isReserved && (
                  <ReserveEndMessage>
                    {t('Reserve End Message')}
                    <ReserveEndDescription>
                      {t('Reserve End Description')}
                    </ReserveEndDescription>
                    <InquiryButton
                      color="primary"
                      onClick={actions.onClickInquiry}
                    >
                      {t('Inquiry')}
                    </InquiryButton>
                  </ReserveEndMessage>
                )}
              </>
            )}
          </BgWrapper>
        </MainContainer>
        <Footer>
          <EventTypeFooter
            eventTypeSlug={props.eventType.slug}
            teamSlug={props.team.slug}
            onClickInquiry={actions.onClickInquiry}
            isIncludeServiceJP={props.eventType.setting.isIncludeServiceJP}
            isInputTokushoho={props.eventType.isInputTokushoho}
            term={props.eventType.term}
            cancelPolicy={props.eventType.cancelPolicy}
            isShowPromotion={props.eventType.isShowPromotion}
          />
        </Footer>
      </PageContainer>
      {props.event && (
        <ConfirmationDialog
          isLoading={state.isConfirmLoading}
          isOpen={state.selectedDatetime !== null}
          onSubmit={actions.onUpdate}
          onCancel={actions.onCloseDialog}
          title={t('Update?')}
        />
      )}
      {state.isDoubleBooked && (
        <ConfirmationDialog
          isOpen={state.isDoubleBooked}
          onSubmit={actions.reloadScreen}
          title={t('double booked')}
          text={t('double booked text')}
          submitLabel={t('double booked label')}
        />
      )}
      {state.exceededInfo && (
        <ConfirmationDialog
          isOpen={Boolean(state.exceededInfo)}
          onSubmit={actions.reloadScreen}
          title={t(`book exceeded ${state.exceededInfo.exceededType}`)}
          text={t(`book exceeded ${state.exceededInfo.exceededType} text`)}
          submitLabel={t('book exceeded label')}
        />
      )}
      {state.isExceedServiceRestrict && (
        <ServiceRestrictedDialog
          isOpen={Boolean(state.isExceedServiceRestrict)}
          onClose={actions.onCloseServiceRestrictedDialog}
          restrictServiceUsages={state.restrictServiceUsages}
        />
      )}
      {state.isShowingCalendarList && (
        <CalendarListDialog
          onClose={actions.onCloseCalendarListDialog}
          googleAuthUrl={props.googleUrl}
          microsoftAuthUrl={props.microsoftUrl}
          {...state.calendarListDialog}
          {...actions.calendarListDialog}
        />
      )}
      {state.isShowingParticipants && (
        <ParticipantsListDialog
          onNext={actions.onClickNextParticipantsListDialog}
          onClose={actions.onCloseParticipantsListDialog}
          selectedDatetime={state.selectedDatetime}
          duration={props.eventType.duration}
          participants={state.showingParticipants}
          color={props.eventType.color}
          isLightColor={state.isLightColor}
        />
      )}
      {state.isShowingInquityForm && (
        <InquiryFormDialog
          onSubmit={actions.onSubmitInquiry}
          onClose={actions.onCloseInquiry}
          color={props.eventType.color}
          isLightColor={state.isLightColor}
          isCompanyHidden={props.eventType.setting.isCompanyHidden}
          isCompanyRequired={props.eventType.setting.isCompanyRequired}
          email={state.email}
          message={state.message}
          name={state.name}
          company={state.company}
          errors={state.errors}
          isLoading={state.isLoading}
          onChange={actions.onChange}
          onBlur={actions.onBlur}
        />
      )}
      {state.snackbarMessage && (
        <Snackbar
          onClose={actions.onCloseSnackbar}
          isOpen={true}
          severity={SeverityType.Error}
          message={state.snackbarMessage}
        />
      )}
      {state.blockTimeReasons.length > 0 && (
        <EventTypeBlockReasonDialog
          reasons={state.blockTimeReasons}
          onClose={actions.onCloseBlockReason}
          viewerUid={viewer?.uid}
        />
      )}
    </ServicePageTemplate>
  );
}

const PageContainer = styled.div`
  background-color: ${white};
  padding: ${spacing(48)} ${spacing(24)} ${spacing(24)};
  min-height: calc(100vh - 210px);
  ${mediaMax.tablet} {
    width: 100%;
    padding: 0;
  }
`;
const BgWrapper = styled.div`
  background-color: ${white};
  border-radius: ${borderRadius(16)};
  border: 1px solid ${gray04};

  ${mediaMax.tablet} {
    border-radius: 0;
    border: none;
  }
`;
const EventMainVisualContainer = styled.div`
  border-radius: ${borderRadius(16)} ${borderRadius(16)} 0 0;
  overflow: hidden;

  ${mediaMax.tablet} {
    border-radius: 0;
    overflow: auto;
  }
`;
const MainContainer = styled.div<{ isFullWidth?: boolean }>`
  position: relative;
  max-width: ${(props) => !props.isFullWidth && '1040px'};
  margin: 0 auto;
`;
const EventHostSummaryBoxDesktopVisible = styled.div`
  display: none;
  ${mediaMax.desktop} {
    padding: 0 ${spacing(24)};
    display: block;
  }
  ${mediaMax.tablet} {
    display: none;
  }
`;
const EventHostSummaryBoxDesktopHidden = styled.div`
  display: block;
  ${mediaMax.desktop} {
    display: none;
  }
  ${mediaMax.tablet} {
    display: block;
  }
`;
const EventTypeContentBox = styled.div<{
  isTopBorder: boolean;
  calendarViewType: CalendarViewType;
}>`
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  background-color: ${white};
  border-bottom-left-radius: ${borderRadius(16)};
  border-bottom-right-radius: ${borderRadius(16)};
  overflow: hidden;

  ${(props) => {
    if (props.calendarViewType === CalendarViewType.Month) {
      return `
        ${mediaMax.tablet} {
          display: block;
          padding-top: 0;
        }
      `;
    }

    return `
      ${mediaMax.desktop} {
        display: block;
        padding-top: 0;
      }
    `;
  }}

  ${({ isTopBorder }) =>
    isTopBorder &&
    `
    border-top-left-radius: ${borderRadius(16)};
    border-top-right-radius: ${borderRadius(16)};
  `}

  ${mediaMax.tablet} {
    padding-bottom: ${spacing(20)};
  }
`;
const FailedView = styled.div`
  position: absolute;
  padding: ${spacing(32)} 0;
  right: ${spacing(48)};
  top: 172px;
  width: 385px;
  height: auto;
  border-radius: ${borderRadius(16)};
  background-color: ${white};
  box-shadow: rgba(0, 0, 0, 0.1) 0px 5px 15px;
  ${mediaMax.tablet} {
    position: static;
    width: 100%;
    height: auto;
  }
`;
const TitleHeadline = styled(Headline)`
  margin-bottom: ${spacing(8)};
  font-size: ${fontSize(20)};
  line-height: 1.4;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const DescriptionContainer = styled.div`
  margin-top: ${spacing(28)};
  ${mediaMax.smartphone} {
    margin-top: ${spacing(16)};
  }
`;
const Description = styled.div`
  white-space: pre-wrap;
  font-size: ${fontSize(14)};
  line-height: 1.6;
`;

const EventInfo = styled.div`
  margin-top: ${spacing(24)};
  ${mediaMax.smartphone} {
    margin-top: ${spacing(12)};
  }
`;
const StyledEventSummaryViewContainer = styled.div`
  padding: ${spacing(32)} ${spacing(24)} ${spacing(16)};
  max-width: 360px;
  ${mediaMax.desktop} {
    padding: 0 ${spacing(24)};
    max-width: initial;
  }
  ${mediaMax.tablet} {
    padding: ${spacing(20)} ${spacing(24)};
  }
`;
const StyledEventReservationViewContainer = styled.div<{
  width: string;
  calendarViewType: CalendarViewType;
}>`
  min-width: ${({ width }) => width};
  min-height: 400px;
  border-left: 1px solid ${gray04};
  flex: 1;

  ${mediaMax.tablet} {
    padding: ${spacing(24)};
  }
  ${mediaMax.smartphone} {
    padding: 0;
    border-radius: ${borderRadius(12)};
  }

  ${(props) => {
    if (props.calendarViewType === CalendarViewType.Month) {
      return `
        height: fit-content;
        border: 1px solid ${gray04(props)};          
        margin: ${spacing(40)};
        border-radius: ${borderRadius(12)};
        overflow: hidden;
        ${mediaMax.tablet} {
          border: none;
          border-left: none;
          margin: 0;
        }
        ${mediaMax.smartphone} {
          padding: ${spacing(24)} ${spacing(8)} !important;
          min-width: 100% !important;
        }
      `;
    }

    return `
      ${mediaMax.desktop} {
        min-width: 100%;
        width: 100%;
        margin-left: 0;
        padding: 0;
        border-left: none;
      }
      ${mediaMax.smartphone} {
        padding-top: ${spacing(24)};
      }
    `;
  }};
`;
const StyledEventReservationViewContent = styled.div<{
  calendarViewType: CalendarViewType;
}>`
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)};
    border: 1px solid ${gray04};
    border-radius: ${borderRadius(12)};
    overflow: hidden;
  }

  ${(props) => {
    if (props.calendarViewType === CalendarViewType.Month) {
      return `
        padding: 0 ${spacing(40)};
        margin: 0 auto !important;
        ${mediaMax.smartphone} {
          padding: 0 ${spacing(8)};
        }
      `;
    } else if (
      [
        CalendarViewType.TwoColumnMonth,
        CalendarViewType.TwoColumnMonthAccent,
      ].includes(props.calendarViewType)
    ) {
      return `
        ${mediaMax.smartphone} {
          margin: 0 ${spacing(8)};
        }
      `;
    }

    return ``;
  }}
`;

const ReserveEndMessage = styled.div`
  margin: 0 ${spacing(48)};
  padding-top: ${spacing(32)};
  padding-bottom: ${spacing(48)};
  border-top: 1px solid ${gray04};
  font-size: ${fontSize(20)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;
const ReserveEndDescription = styled.div`
  font-size: ${fontSize(12)};
  font-weight: initial;
`;

const InquiryButton = styled(Button)`
  margin-top: ${spacing(16)};
  border-color: ${primary};
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('bold')};
  display: flex;
  align-items: center;
`;

const HeaderTitle = styled.div`
  display: flex;

  ${mediaMax.smartphone} {
    flex-direction: column;
  }
`;
const HostDiagnosticTitle = styled.span`
  border-right: 1px solid ${white};
  padding-right: ${spacing(16)};
  margin-right: ${spacing(16)};

  ${mediaMax.smartphone} {
    border-right: none;
    padding-right: 0;
    margin-right: 0;
    margin-bottom: ${spacing(4)};
    font-size: ${fontSize(10)};
  }
`;
const Footer = styled.div`
  margin: ${spacing(24)} auto 0;
  ${mediaMax.tablet} {
    padding-top: ${spacing(24)};
    border-top: 1px solid ${gray04};
  }
`;

const StyledRequiredServiceContainer = styled.div`
  padding: ${spacing(16)};
  border-radius: ${borderRadius(12)};
  margin-top: ${spacing(24)};
  background-color: ${blueGrayPale};
`;
const SectionTitle = styled.div`
  margin-bottom: ${spacing(12)};
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
    margin-bottom: ${spacing(6)};
  }
`;
